import { displayDetails } from './checkEvidence';
import { PLACEHOLDER } from './placeholder';

/**
 * Read model for an ISFR fund-request record.
 *
 * Reads the row the ISFR agent persists to chronos_validated_fundrequests
 * (ChronosAgentCoreAgents configuration/isfr_validation_agent/tools/
 * stage_5_scoring_recommendation.py, _write_validated_fundrequest) and turns it
 * into what the ISFR review view renders. Pure functions only, so every display
 * rule is unit-tested without rendering.
 *
 * Two rules hold throughout:
 *
 *   - A field that is absent and a field that is present but unreadable are
 *     different states, and both are reported. Neither is quietly replaced by a
 *     default, because an empty section reads as "nothing to see" when the truth
 *     is "this run did not record it" or "it could not be parsed".
 *   - Outcome framing comes from the persisted category states, never from the
 *     verdict string alone. The verdict collapses a request that passed
 *     everything the agent automates into the same recommend_rejection as a
 *     request with a real problem; the category rollup is what tells them apart.
 */

export const VERDICTS = ['recommend_approval', 'recommend_rejection', 'needs_review'];

/** Category states, as the agent's decision layer names them. */
export const CATEGORY_STATE = {
  PASS: 'pass',
  FAILED: 'failed',
  PENDING: 'pending',
  NOT_REVIEWED: 'not_reviewed',
  NOT_APPLICABLE: 'not_applicable',
};

/** The category role whose members a human verifies. */
export const HUMAN_VERIFY_ROLE = 'human_verify';

/** The category role judged on its own: a failure there blocks approval. */
export const CONCERN_ROLE = 'concern';

/** What kind of result a record is, stated before any detail. */
export const OUTCOME = {
  APPROVED: 'approved',
  CLEAN_PENDING: 'clean_pending',
  PROBLEM_FOUND: 'problem_found',
  CLEAN_ROUTED_TO_HUMAN: 'clean_routed_to_human',
  REVIEW_INCOMPLETE: 'review_incomplete',
  UNDETERMINED: 'undetermined',
};

/**
 * Does a category in this state withhold approval? Same rule as the agent's
 * decision.py `category_blocks`: pass and not_applicable are clear, every
 * other state blocks.
 */
export const categoryBlocks = (state) =>
  state !== CATEGORY_STATE.PASS && state !== CATEGORY_STATE.NOT_APPLICABLE;

/**
 * Read one persisted JSON-string field.
 *
 * @returns {{status: 'absent'|'ok'|'unreadable', value: any}}
 *   'absent' when the record does not carry the field, 'unreadable' when it
 *   does but the value does not parse. An already-parsed object or array is
 *   accepted as-is.
 */
export function readJsonField(record, field) {
  const raw = record ? record[field] : undefined;
  if (raw === undefined || raw === null || raw === '') {
    return { status: 'absent', value: null };
  }
  if (typeof raw === 'object') {
    return { status: 'ok', value: raw };
  }
  if (typeof raw !== 'string') {
    return { status: 'unreadable', value: null };
  }
  try {
    return { status: 'ok', value: JSON.parse(raw) };
  } catch (e) {
    return { status: 'unreadable', value: null };
  }
}

/**
 * A persisted list of strings. A field that parses to something other than an
 * array is unreadable, not empty.
 */
function readStringList(record, field) {
  const read = readJsonField(record, field);
  if (read.status !== 'ok') return read;
  if (!Array.isArray(read.value)) return { status: 'unreadable', value: null };
  return {
    status: 'ok',
    value: read.value.filter((item) => typeof item === 'string' && item.trim() !== ''),
  };
}

/**
 * The verdict as one of the three platform values, or null when the record
 * carries something else. `approval_status` is the persisted verdict;
 * `approval_summary.recommendation` is the same value and covers a record that
 * lacks the top-level field.
 */
export function normaliseVerdict(record) {
  const raw = (record && (record.approval_status || record.approval_summary?.recommendation)) || '';
  const value = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
  return VERDICTS.includes(value) ? value : null;
}

/** "recommend_rejection" -> "RECOMMEND REJECTION". */
export const verdictLabel = (verdict) => (verdict ? verdict.replace(/_/g, ' ').toUpperCase() : '');

/**
 * Orders check ids the way the review criteria number them: "1.4" before
 * "1.16", "3.3-T1" before "3.3-T2". The persisted lists are string-sorted, which
 * puts "1.10" before "1.2".
 */
export function compareCheckIds(a, b) {
  const parts = (id) => String(id).split(/[.-]/);
  const pa = parts(a);
  const pb = parts(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    if (pa[i] === undefined) return -1;
    if (pb[i] === undefined) return 1;
    const na = Number(pa[i]);
    const nb = Number(pb[i]);
    const bothNumeric = pa[i] !== '' && pb[i] !== '' && !Number.isNaN(na) && !Number.isNaN(nb);
    const order = bothNumeric ? na - nb : pa[i].localeCompare(pb[i]);
    if (order !== 0) return order;
  }
  return 0;
}

/**
 * Categories in the order a reviewer works through them: human verification
 * first, since it is the work only the reviewer can do, then the rest in the
 * order the program's review criteria declare them. An advisory category keeps
 * its declared place beside the checks it relates to.
 */
/** The human verification category: the reviewer's own checks, not an advisory one. */
export const isHumanVerification = (c) => c.role === HUMAN_VERIFY_ROLE && !c.advisory;
export const orderForReview = (categories) => [
  ...categories.filter(isHumanVerification),
  ...categories.filter((c) => !isHumanVerification(c)),
];

/**
 * The categories grouped the way the Approval Summary's breakdown lists them:
 * what a human must verify first, then what is not clear (failed, or not fully
 * reviewed), then what passed, then what does not apply at the request's tier.
 * Review order holds within each group, and only groups with members are kept.
 * A state the view does not know blocks approval, so it is listed as not clear.
 */
export function groupCategoriesByState(categories) {
  const groups = [
    { key: 'to_verify', test: (c) => c.state === CATEGORY_STATE.PENDING },
    { key: 'not_clear', test: (c) => categoryBlocks(c.state) && c.state !== CATEGORY_STATE.PENDING },
    { key: 'passed', test: (c) => c.state === CATEGORY_STATE.PASS },
    { key: 'not_applicable', test: (c) => c.state === CATEGORY_STATE.NOT_APPLICABLE },
  ];
  return groups
    .map(({ key, test }) => ({ key, categories: categories.filter(test) }))
    .filter((g) => g.categories.length > 0);
}

/**
 * The category rollup as an ordered list, in review order (orderForReview).
 * Check ids within a category are put in criteria-number order.
 *
 * @returns {{status: 'absent'|'ok'|'unreadable', categories: object[],
 *   anomalies: {unmapped: string[], infrastructure: string[], invalidResults: string[]}}}
 */
// Checks the agent still runs and records, but the reviewer is not shown in v1:
// cross-partner account reuse and resubmission consistency.
// Left out of every category the page reads, so its lists and counts agree.
// The agent marks each such check `shown: false`; this list stands in on a
// record from before it did (no check carries the mark).
export const NOT_SHOWN_IN_V1 = new Set(['1.17', '5.11']);

function notShownIds(record) {
  const read = readJsonField(record, 'reviewer_package_json');
  const stages = read.status === 'ok' && read.value && read.value.stage_summary;
  const entries = stages && typeof stages === 'object' && !Array.isArray(stages)
    ? Object.values(stages).flatMap((checks) => (Array.isArray(checks) ? checks : []))
      .filter((c) => c && typeof c === 'object')
    : [];
  if (!entries.some((c) => 'shown' in c)) return NOT_SHOWN_IN_V1;
  return new Set(entries.filter((c) => c.shown === false).map((c) => String(c.check_id)));
}

export function readCategories(record) {
  const read = readJsonField(record, 'category_rollup_json');
  const empty = { unmapped: [], infrastructure: [], invalidResults: [] };
  if (read.status !== 'ok') return { status: read.status, categories: [], anomalies: empty };

  const cats = read.value && read.value.categories;
  if (!cats || typeof cats !== 'object' || Array.isArray(cats)) {
    return { status: 'unreadable', categories: [], anomalies: empty };
  }
  const list = (value) => (Array.isArray(value) ? value.map(String) : []);
  const ordered = (value) => list(value).sort(compareCheckIds);
  const notShown = notShownIds(record);
  const categories = orderForReview(Object.entries(cats)
    .filter(([, c]) => c && typeof c === 'object')
    .map(([key, c]) => ({
      key,
      label: c.label || key,
      role: c.role,
      advisory: c.advisory === true,
      state: c.state,
      members: ordered(c.members).filter((id) => !notShown.has(id)),
      notApplicable: ordered(c.not_applicable).filter((id) => !notShown.has(id)),
    })));
  return {
    status: 'ok',
    categories,
    anomalies: {
      unmapped: list(read.value.unmapped),
      infrastructure: list(read.value.infrastructure),
      invalidResults: list(read.value.invalid_results),
    },
  };
}

/**
 * What kind of result this is. Mirrors the partition the agent's executive
 * summary and its is_clean_pending use, so the view and the agent's own
 * wording cannot disagree. Each framing is stated only when the stored record
 * supports it, never from the verdict string alone, because records written
 * before the agent stored categories carry the same verdict values with
 * different meanings (an approval then could rest on deferred checks):
 *
 *   - needs_review is a clean hand-off when the never-auto-approve gate turned
 *     a clean approval into needs_review (auto_approval_suppressed), and an
 *     our-side failure when the record carries what caused one (an errored
 *     check, or a result the rollup could not place).
 *   - An approval is "every check passed" only when every category is clear.
 *   - A rejection is a substantive problem when any category FAILED, or any
 *     category outside human verification blocks, and clean-but-pending when
 *     the only blockers are human verification categories that are pending,
 *     not failed.
 *   - Anything else is left unframed, and the view says so instead of guessing.
 *
 * @param {object} evidence `{autoApprovalSuppressed, hasErroredChecks, hasRollupAnomalies}`
 * @returns {{kind: string, notClear: string[], pendingHuman: string[]}}
 */
export function deriveOutcome(verdict, categories, evidence = {}) {
  const result = (kind, notClear = [], pendingHuman = []) => ({ kind, notClear, pendingHuman });
  const hasCategories = Array.isArray(categories) && categories.length > 0;
  if (verdict === 'needs_review') {
    if (evidence.autoApprovalSuppressed === true) return result(OUTCOME.CLEAN_ROUTED_TO_HUMAN);
    if (evidence.hasErroredChecks || evidence.hasRollupAnomalies) {
      return result(OUTCOME.REVIEW_INCOMPLETE);
    }
    return result(OUTCOME.UNDETERMINED);
  }
  if (verdict === 'recommend_approval') {
    return hasCategories && !categories.some((c) => categoryBlocks(c.state))
      ? result(OUTCOME.APPROVED)
      : result(OUTCOME.UNDETERMINED);
  }
  if (verdict !== 'recommend_rejection' || !hasCategories) {
    return result(OUTCOME.UNDETERMINED);
  }
  const notClear = categories
    .filter((c) => c.state === CATEGORY_STATE.FAILED
      || (c.role !== HUMAN_VERIFY_ROLE && categoryBlocks(c.state)))
    .map((c) => c.label);
  if (notClear.length > 0) return result(OUTCOME.PROBLEM_FOUND, notClear);
  const pendingHuman = categories
    .filter((c) => c.role === HUMAN_VERIFY_ROLE && categoryBlocks(c.state))
    .map((c) => c.label);
  if (pendingHuman.length > 0) return result(OUTCOME.CLEAN_PENDING, [], pendingHuman);
  return result(OUTCOME.UNDETERMINED);
}

/**
 * Per-check results from the persisted reviewer package, keyed by check id.
 *
 * Each check also carries `evidence`, what it worked from and concluded (see
 * checkEvidence.js), and a reasoning check carries `evidence_quotes`, the
 * submission phrases the agent cited as its evidence. They are kept exactly as stored. Nothing yet
 * checks them against the submission itself, so the view presents them as the
 * agent's citations, not as confirmed extracts.
 *
 * @returns {{status: 'absent'|'ok'|'unreadable', byId: Map<string, object>,
 *   reviewerComment: string}}
 */
export function readChecks(record) {
  const read = readJsonField(record, 'reviewer_package_json');
  if (read.status !== 'ok') return { status: read.status, byId: new Map(), reviewerComment: '' };
  const stages = read.value && read.value.stage_summary;
  if (!stages || typeof stages !== 'object' || Array.isArray(stages)) {
    return { status: 'unreadable', byId: new Map(), reviewerComment: '' };
  }
  const byId = new Map();
  Object.entries(stages).forEach(([stage, checks]) => {
    (Array.isArray(checks) ? checks : []).forEach((c) => {
      if (!c || c.check_id === undefined || c.check_id === null) return;
      const id = String(c.check_id);
      const evidence = c.evidence && typeof c.evidence === 'object' && !Array.isArray(c.evidence)
        ? c.evidence : {};
      byId.set(id, {
        id,
        name: c.name || '',
        result: String(c.result || '').toUpperCase(),
        details: displayDetails(id, c.details, evidence),
        evidenceQuotes: Array.isArray(c.evidence_quotes)
          ? c.evidence_quotes.filter((q) => typeof q === 'string' && q.trim() !== '')
          : [],
        evidence,
        // What the check read and could not use, and why a person verifies it.
        // Absent on records from before the agent saved it.
        coverage: c.coverage && typeof c.coverage === 'object' && !Array.isArray(c.coverage) ? c.coverage : null,
        stage,
      });
    });
  });
  const reviewerComment = typeof read.value.reviewer_comment === 'string'
    ? read.value.reviewer_comment : '';
  return { status: 'ok', byId, reviewerComment };
}

/**
 * A category's checks sorted by what a reviewer has to do with them.
 *
 *   failed        FAIL
 *   flagged       FLAG (did not affect the verdict)
 *   incomplete    DEFERRED, UNVERIFIED or PARTIAL: did not complete, never a
 *                 pass. In human verification these are the items to verify
 *                 by hand.
 *   passed        PASS
 *   other         any other stored result, shown with that result
 *   assisting     in an advisory category, every member that reported a
 *                 result: the agent's review assists the reviewer, who
 *                 verifies the item either way, so its result is not a pass
 *                 or a finding
 *   notApplicable checks that do not apply at the request's tier
 *   missing       member ids with no per-check result on the record
 *
 * Members keep criteria-number order within each group.
 */
export function groupCategoryChecks(category, checksById) {
  const groups = {
    failed: [], flagged: [], incomplete: [], passed: [], other: [], assisting: [], notApplicable: [], missing: [],
  };
  category.members.forEach((id) => {
    const check = checksById.get(id);
    if (!check) {
      groups.missing.push(id);
      return;
    }
    const incomplete = ['DEFERRED', 'UNVERIFIED', 'PARTIAL'].includes(check.result);
    if (category.advisory && !incomplete) {
      groups.assisting.push(check);
      return;
    }
    switch (check.result) {
      case 'FAIL': groups.failed.push(check); break;
      case 'FLAG': groups.flagged.push(check); break;
      case 'PASS': groups.passed.push(check); break;
      case 'DEFERRED':
      case 'UNVERIFIED':
      case 'PARTIAL': groups.incomplete.push(check); break;
      default: groups.other.push(check);
    }
  });
  category.notApplicable.forEach((id) => {
    groups.notApplicable.push(checksById.get(id) || { id, name: '', result: '', details: '', evidenceQuotes: [] });
  });
  return groups;
}

/**
 * The check counts the Validation Summary states, over the checks a reviewer
 * sees: each category's members. Checks the review criteria hide and checks
 * that do not apply at the request's tier are in no category's members, so
 * they are not counted. Only a clean PASS counts as passed. Everything else
 * needs the reviewer's attention, flags included: a flag did not change the
 * verdict, but it is worth a human look. A FAIL or FLAG in an advisory
 * category is the reviewer's to verify, not a problem found in the request,
 * the same split the agent's own counts make.
 *
 * @returns {null|{total: number, passed: number, attention: number,
 *   failed: number, flagged: number, toVerify: number, notCompleted: number,
 *   other: number, noResult: number}}
 *   null when the record stores no per-check results to count.
 *   toVerify: incomplete checks in human verification, the reviewer's own job.
 *   notCompleted: incomplete checks anywhere else.
 */
/**
 * The human verification items a reviewer does by hand: a check that has not
 * passed and that the agent records a reason for a person to verify, except an
 * approval (decided by people), which the Approval Summary's routing note
 * carries. A record from before the agent saved those reasons gives the checks
 * that did not complete.
 */
const INCOMPLETE_RESULTS = ['DEFERRED', 'UNVERIFIED', 'PARTIAL'];
export function humanVerifyItems(category, checksById) {
  return category.members.map((id) => checksById.get(id)).filter((check) => {
    if (!check || check.result === 'PASS') return false;
    const reason = check.coverage && check.coverage.verify_reason;
    return reason ? reason.code !== 'decided_by_people' : INCOMPLETE_RESULTS.includes(check.result);
  });
}

/**
 * A flag the agent records as decided by people (the approvals the request needs)
 * is a routing step, not a flag: the page says "Proceed to PSA" instead.
 */
export const isRoutingStep = (check) => Boolean(check && check.result === 'FLAG' && check.coverage
  && check.coverage.verify_reason && check.coverage.verify_reason.code === 'decided_by_people');

export function countChecks(categories, checks) {
  if (checks.status !== 'ok' || categories.length === 0) return null;
  const counts = {
    total: 0, passed: 0, failed: 0, flagged: 0, toVerify: 0, notCompleted: 0, other: 0, noResult: 0, notAutomated: 0, routed: 0,
  };
  categories.forEach((category) => {
    const groups = groupCategoryChecks(category, checks.byId);
    const assisting = (result) => groups.assisting.filter((c) => result(c.result)).length;
    // In human verification, what the reviewer does by hand counts as to verify,
    // whatever result the agent recorded for it (a plan-date flag, a deferral).
    const verify = isHumanVerification(category) ? new Set(humanVerifyItems(category, checks.byId).map((c) => c.id)) : new Set();
    const notVerify = (list) => list.filter((c) => !verify.has(c.id)).length;
    counts.total += category.members.length;
    counts.passed += groups.passed.length + assisting((r) => r === 'PASS');
    counts.failed += notVerify(groups.failed);
    counts.flagged += notVerify(groups.flagged.filter((c) => !isRoutingStep(c)));
    counts.routed += groups.flagged.filter(isRoutingStep).length;
    counts.toVerify += assisting((r) => r === 'FAIL' || r === 'FLAG') + verify.size;
    // What the Human Verification tab lists is not automated yet, and is named apart.
    counts.notAutomated += verify.size;
    if (category.role === HUMAN_VERIFY_ROLE) counts.toVerify += notVerify(groups.incomplete);
    else counts.notCompleted += groups.incomplete.length;
    counts.other += groups.other.length + assisting((r) => !['PASS', 'FAIL', 'FLAG'].includes(r));
    counts.noResult += groups.missing.length;
  });
  counts.attention = counts.total - counts.passed;
  return counts;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * countChecks as the sentences the Validation Summary states, so the number is
 * never the only thing said: how many passed; what requires the reviewer, by
 * kind; when some checks are not automated yet (the Human Verification tab's
 * list), how many; and the flags apart, since a flag did not change the verdict.
 * The first line counts none of the flags, the routing step (Proceed to PSA) or
 * the checks not automated yet. A
 * line with nothing to say is '' (the attention line says "none").
 */
export function describeCounts(counts) {
  const notAutomated = counts.notAutomated || 0;
  const toVerify = counts.toVerify - notAutomated;
  const counted = counts.total - notAutomated - counts.flagged - (counts.routed || 0);
  const kinds = [
    counts.failed && `${counts.failed} failed`,
    toVerify && `${toVerify} to verify by hand`,
    counts.notCompleted && `${counts.notCompleted} not completed`,
    counts.other && `${counts.other} with another result`,
    counts.noResult && `${counts.noResult} with no stored result`,
  ].filter(Boolean).join(', ');
  return {
    passed: notAutomated
      ? `${counts.passed} of ${plural(counted, 'automated check', 'automated checks')} passed`
      : `${counts.passed} of ${plural(counted, 'check', 'checks')} passed`,
    attention: `Automated checks requiring your attention: ${kinds || 'none'}.`,
    notAutomated: notAutomated ? `Not automated yet: ${plural(notAutomated, 'check', 'checks')}.` : '',
    worthVerifying: counts.flagged ? `Non-blocking, worth verifying: ${counts.flagged} flagged.` : '',
  };
}

/**
 * The checks behind describeCounts' lines, by name, each {check, category, kind}:
 * `needed` requires the reviewer (a failure, an advisory result or a check that
 * did not complete in human verification to verify by hand, a check that did not
 * complete, another result), `worthVerifying` holds the flags. What the Human
 * Verification tab lists is in neither. A member with no stored result has no
 * name; describeCounts counts it.
 */
export function attentionChecks(categories, checks) {
  const out = { needed: [], worthVerifying: [] };
  if (checks.status !== 'ok') return out;
  categories.forEach((category) => {
    const groups = groupCategoryChecks(category, checks.byId);
    const verify = isHumanVerification(category) ? new Set(humanVerifyItems(category, checks.byId).map((c) => c.id)) : new Set();
    const add = (list, kind, into) => list.filter((c) => !verify.has(c.id)).forEach((check) => into.push({ check, category, kind }));
    add(groups.failed, 'failed', out.needed);
    add(groups.assisting.filter((c) => c.result === 'FAIL' || c.result === 'FLAG'), 'to verify by hand', out.needed);
    add(groups.incomplete, category.role === HUMAN_VERIFY_ROLE ? 'to verify by hand' : 'not completed', out.needed);
    add(groups.other, 'another result', out.needed);
    add(groups.assisting.filter((c) => !['PASS', 'FAIL', 'FLAG'].includes(c.result)), 'another result', out.needed);
    add(groups.flagged.filter((c) => !isRoutingStep(c)), 'flagged', out.worthVerifying);
  });
  return out;
}

/**
 * The agent's executive summary as labelled sections, so the view can lay it
 * out instead of printing one block. The summary is a run of bold-labelled
 * lines ("**Reasons:**"), each with text after the label, an indented list
 * beneath it, or both. A label's colon may sit inside the bold or after it
 * ("**Flags to keep in mind** (these did not affect the verdict):").
 *
 * A line that is neither a label nor a list item continues the entry above
 * it: model-written flag details can run over several lines.
 *
 * @returns {null|{label: string, text: string, items: string[]}[]}
 *   null when text comes before the first label. The view then renders the
 *   text as written, so nothing the agent wrote is lost to a layout this parser
 *   does not know.
 */
export function readExecutiveSummary(text) {
  if (typeof text !== 'string' || text.trim() === '') return null;
  const sections = [];
  const lines = text.split('\n').map((line) => line.trim()).filter((line) => line !== '');
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const labelled = line.match(/^\*\*(.+?)\*\*(.*)$/);
    const item = line.match(/^[-*]\s+(.+)$/);
    if (labelled) {
      const insideColon = labelled[1].trim().endsWith(':');
      let rest = labelled[2].trim();
      if (!insideColon && rest.startsWith(':')) rest = rest.slice(1).trim();
      if (!insideColon && rest.endsWith(':')) rest = rest.slice(0, -1).trim();
      sections.push({ label: labelled[1].trim().replace(/:$/, ''), text: rest, items: [] });
    } else if (sections.length === 0) {
      return null;
    } else if (item) {
      sections[sections.length - 1].items.push(item[1].trim());
    } else {
      const section = sections[sections.length - 1];
      if (section.items.length > 0) {
        section.items[section.items.length - 1] += `\n${line}`;
      } else {
        section.text = section.text ? `${section.text}\n${line}` : line;
      }
    }
  }
  return sections.length > 0 ? sections : null;
}

/** The first line of a check's details, the one-line form shown in a summary. */
export const firstLine = (text) => (typeof text === 'string' ? text.split('\n')[0].trim() : '');

/**
 * Why a check that did not complete stopped, in its own recorded words: the
 * reason its evidence gives, else its one-line details.
 */
export const incompleteReason = (check) => {
  const reason = check && check.evidence && typeof check.evidence.reason === 'string'
    ? check.evidence.reason.trim() : '';
  return reason || firstLine(check && check.details);
};

/**
 * In an advisory category, why the agent's review did not run, one line per
 * check that did not complete (a diagram that was not attached, say). The
 * category stays the reviewer's to verify; this says what the agent had.
 */
export const advisoryDetails = (category, checksById) => (category.advisory
  ? groupCategoryChecks(category, checksById).incomplete.map(incompleteReason).filter(Boolean)
  : []);

/**
 * Triggered flags, each with the category it relates to. A flag is a check
 * that reported FLAG; checks that do not apply at this request's tier are
 * excluded, the same exclusion the agent applies before it lists flags, and
 * so are advisory categories, whose results are the reviewer's to verify.
 */
export function deriveFlags(categories, checksById) {
  const flags = [];
  categories.filter((c) => !c.advisory).forEach((c) => {
    c.members.forEach((id) => {
      const check = checksById.get(id);
      if (check && check.result === 'FLAG' && !isRoutingStep(check)) {
        flags.push({ ...check, categoryKey: c.key, categoryLabel: c.label });
      }
    });
  });
  return flags;
}

/**
 * Checks that errored, and FAILs no decision rule handled. They mean different
 * things: an errored check did not complete and routes the request to
 * needs_review, while an unhandled FAIL still fails its category and so
 * rejects, with only a generic reason behind it. The view keeps them apart.
 */
export function readIncompleteChecks(record) {
  const entries = (field, messageKey) => {
    const read = readJsonField(record, field);
    if (read.status !== 'ok') return { status: read.status, value: [] };
    if (!Array.isArray(read.value)) return { status: 'unreadable', value: [] };
    return {
      status: 'ok',
      value: read.value
        .filter((c) => c && typeof c === 'object')
        .map((c) => ({
          id: c.check_id === undefined || c.check_id === null ? '' : String(c.check_id),
          name: c.name || '',
          message: typeof c[messageKey] === 'string' ? c[messageKey] : '',
        })),
    };
  };
  return {
    errored: entries('errored_checks_json', 'error_message'),
    unhandledFails: entries('unhandled_failed_checks_json', 'fail_message'),
  };
}

/**
 * The agent's executive summary, made safe for the shared MarkdownRenderer.
 *
 * The summary writes a bold label line straight after a bulleted list
 * ("  - item" then "**Issues:** ..."). The renderer treats any non-blank line
 * after a list item as a continuation of that list, so without a blank line
 * every later section folds into the first list. A blank line is inserted
 * before a bold label line that follows a list item, and a placeholder is set in
 * bold; nothing else changes.
 */
export function toRenderableNarrative(text) {
  if (typeof text !== 'string' || text.trim() === '') return '';
  const out = [];
  let previous = '';
  text.split('\n').forEach((line) => {
    const trimmed = line.trim();
    const previousIsListItem = previous.startsWith('- ') || previous.startsWith('* ');
    if (trimmed.startsWith('**') && previousIsListItem) out.push('');
    // A placeholder is set in bold so it stands out (the renderer has no highlight),
    // unless the line already carries bold, which a nested marker would break.
    out.push(trimmed.includes('**') ? line : line.split(PLACEHOLDER).map((piece, i) => (i % 2 === 1 ? `**${piece}**` : piece)).join(''));
    if (trimmed !== '') previous = trimmed;
  });
  return out.join('\n');
}

/**
 * The executive summary without a confidence line. Reviewers are not shown the
 * run's confidence label, and summaries saved by agent versions that wrote one
 * still carry a "**Confidence:** ..." line.
 */
export const withoutConfidence = (text) => (typeof text === 'string'
  ? text.split('\n').filter((line) => !/^\s*\*\*Confidence:\*\*/.test(line)).join('\n')
  : '');

/** Everything the ISFR review view renders, read once from the record. */
export function readIsfrRecord(record) {
  const summary = (record && record.approval_summary) || {};
  const verdict = normaliseVerdict(record);
  const rollup = readCategories(record);
  const checks = readChecks(record);
  const incomplete = readIncompleteChecks(record);
  const { unmapped, infrastructure, invalidResults } = rollup.anomalies;
  const asStrings = (value) => (Array.isArray(value)
    ? value.filter((v) => typeof v === 'string' && v.trim() !== '')
    : []);
  return {
    verdict,
    rawVerdict: (record && (record.approval_status || summary.recommendation)) || '',
    verdictLabel: verdictLabel(verdict),
    outcome: deriveOutcome(verdict, rollup.categories, {
      autoApprovalSuppressed: record ? record.auto_approval_suppressed : undefined,
      hasErroredChecks: incomplete.errored.value.length > 0,
      hasRollupAnomalies: unmapped.length + infrastructure.length + invalidResults.length > 0,
    }),
    rollup,
    checks,
    counts: countChecks(rollup.categories, checks),
    flags: deriveFlags(rollup.categories, checks.byId),
    narrative: toRenderableNarrative(withoutConfidence(record ? record.agent_narrative : '')),
    executiveSummary: readExecutiveSummary(withoutConfidence(record ? record.agent_narrative : '')),
    reviewerReasons: readStringList(record, 'reviewer_reasons_json'),
    reviewerActions: readStringList(record, 'reviewer_action_items_json'),
    partnerFindings: asStrings(summary.key_findings),
    partnerActions: asStrings(summary.required_actions),
    incomplete,
    originalRecommendation: originalRecommendationLabel(record),
  };
}

/** The pre-escalation verdict in display form; an unrecognised value as stored. */
function originalRecommendationLabel(record) {
  const raw = record && record.original_recommendation;
  if (!raw) return '';
  return verdictLabel(normaliseVerdict({ approval_status: raw })) || String(raw);
}
